import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { SessionRepository } from '@app-core/state/session.repository';
import { combineLatest, map } from 'rxjs';
import { LanguageRepository } from '@app-core/state/language.repository';
import { CountryRepository } from 'src/app/entities/countries/state/country.repository';

/**
 * Used only to save the url that the user wanted to go to, such as for example: `/register?invite={token}`
 *
 * We don't need to save this route if we already have saved language and country.
 */
export const rememberRouteGuard: CanActivateFn = (route, state) => {
  const sessionRepo = inject(SessionRepository);
  const countryRepo = inject(CountryRepository);

  const languageRepo = inject(LanguageRepository);

  const countryID = route.queryParamMap.get('country');
  const invite = route.queryParamMap.get('invite');

  if (countryID) {
    // if a `country` query param exists, we might as well set the active country
    countryRepo.setActiveCountryByID(+countryID);
  }

  return combineLatest([
    sessionRepo.savedRoute$,
    countryRepo.countryParam$,
    languageRepo.hasUserChosenLanguage$,
  ]).pipe(
    map(([savedRoute, countryParam, hasChosenLanguage]) => {
      /*
        Problem 1:
        When we have `!savedRoute || !countryParam` then whenever we go to /register route
        it gets saved in the `savedRoute` and once we leave to /login and proceed to login
        we get redirected back to /register as that is what we saved (and by leave, i mean
        clicking the /login link, not registering, as that actually deletes the savedRoute).

        Problem 2:
        If we set `!savedRoute && !countryParam` then we only save the route when we don't have
        the `countryParam`. Which means if a fresh user is linked to for example: `/register?country=2` 
        that we will have a country param, so we won't save the route, and since the user gets 
        redirected to language selection, once he chooses the language, he will be 
        continuing to /login instead of the wanted `/register?country=2`.
      */

      // Solution for now is checking the savedRoute and countryParam or chosenLanguage or invite query param.
      // Should be checked if there is another potential problem like the ones above.
      if ((!savedRoute && (!countryParam || !hasChosenLanguage)) || invite) {
        sessionRepo.setStoreProp('savedRoute', state.url);
      }

      return true;
    }),
  );
};
